import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { SubscriptionModal } from '../SubscriptionModal/SubscriptionModal';
import { LegalModal } from '../Legal/LegalModal';
import './AuthBadge.css';

// Sole admin for now — kept in sync with ADMIN_EMAIL in functions/index.js.
// Only gates whether the menu item shows; the actual admin action is
// re-checked server-side against the caller's verified ID token.
const ADMIN_EMAIL = 'danishajaib93@gmail.com';

// Read by App.jsx once sign-in completes, to auto-open BusinessApplicationModal
// instead of assuming the new account will find its own way to Profile.
// localStorage (not sessionStorage) — signInWithRedirect fully navigates the
// page away and back, and can spawn a browsing context (in-app WebViews)
// that doesn't reliably keep sessionStorage across that gap.
export const BUSINESS_SIGNUP_INTENT_KEY = 'motoroam_business_signup_intent';

export function AuthBadge({ onOpenProfile, onOpenRides, onOpenAdmin, onOpenDashboard }) {
  const { user, profile, signIn, sendEmailLink, signOut, authError, isSubscribed } = useAuth();
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [legalTab, setLegalTab] = useState(null); // 'terms' | 'privacy' | 'refund'
  const [signinOpen, setSigninOpen] = useState(false);
  const [businessIntent, setBusinessIntent] = useState(false);
  const [email, setEmail] = useState('');
  const [linkState, setLinkState] = useState('idle'); // idle | sending | sent | error
  const containerRef = useRef(null);

  // Close dropdown on outside click — also clears an abandoned business-signup
  // intent so it can't wrongly surface the application modal on some later,
  // unrelated sign-in.
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setOpen(false);
        setSigninOpen(false);
        if (businessIntent) {
          setBusinessIntent(false);
          try { localStorage.removeItem(BUSINESS_SIGNUP_INTENT_KEY); } catch { /* ignore */ }
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [businessIntent]);

  async function handleSendLink(e) {
    e.preventDefault();
    const addr = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) { setLinkState('error'); return; }
    setLinkState('sending');
    try {
      await sendEmailLink(addr);
      setLinkState('sent');
    } catch (err) {
      console.error('sendEmailLink failed:', err.code, err.message);
      setLinkState('error');
    }
  }

  if (!user) {
    return (
      <div ref={containerRef} style={{ position: 'fixed', top: 16, right: 16, zIndex: 500, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        <button className="auth-badge auth-badge--signin" onClick={() => setSigninOpen(v => !v)}>
          <span>Sign in</span>
        </button>

        {signinOpen && (
          <div className="auth-signin">
            {businessIntent && (
              <div className="auth-signin__business-note">
                Signing in to apply as a business — you'll fill out the application right after.
              </div>
            )}
            <button className="auth-signin__google" onClick={signIn}>
              <GoogleIcon />
              Continue with Google
            </button>
            <div className="auth-signin__or"><span>or</span></div>
            {linkState === 'sent' ? (
              <div className="auth-signin__sent">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/></svg>
                <div>
                  <div className="auth-signin__sent-title">Check your inbox</div>
                  <div className="auth-signin__sent-sub">
                    We emailed a sign-in link to {email.trim()}. Don't see it? Check your spam/junk folder.
                  </div>
                </div>
              </div>
            ) : (
              <form className="auth-signin__email" onSubmit={handleSendLink}>
                <input
                  type="email"
                  className="auth-signin__input"
                  placeholder="you@email.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); if (linkState === 'error') setLinkState('idle'); }}
                  autoFocus
                />
                <button type="submit" className="auth-signin__send" disabled={linkState === 'sending'}>
                  {linkState === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
                </button>
                {linkState === 'error' && (
                  <span className="auth-signin__err">Enter a valid email and try again.</span>
                )}
              </form>
            )}
          </div>
        )}

        {authError && (
          <div className="auth-badge__error">{authError}</div>
        )}
        {!signinOpen && (
          <button
            type="button"
            className="auth-badge__legal-link"
            onClick={() => {
              try { localStorage.setItem(BUSINESS_SIGNUP_INTENT_KEY, '1'); } catch { /* private mode */ }
              setBusinessIntent(true);
              setSigninOpen(true);
            }}
          >
            Own a touring business or coach riders? Sign up as a business →
          </button>
        )}
        <p className="auth-badge__legal">
          By signing in you agree to our{' '}
          <button className="auth-badge__legal-link" onClick={() => setLegalTab('terms')}>Terms</button>
          {' & '}
          <button className="auth-badge__legal-link" onClick={() => setLegalTab('privacy')}>Privacy Policy</button>.
        </p>
        {legalTab && <LegalModal initialTab={legalTab} onClose={() => setLegalTab(null)} />}
      </div>
    );
  }

  const initials = user.displayName
    ? user.displayName.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()
    : user.email?.[0]?.toUpperCase() ?? '?';

  return (
    <div className="auth-badge auth-badge--user" ref={containerRef}>
      <button className="auth-badge__user-btn" onClick={() => setOpen(v => !v)}>
        {user.photoURL
          ? <img className="auth-badge__avatar-img" src={user.photoURL} alt="" width="22" height="22" />
          : <span className="auth-badge__avatar">{initials}</span>
        }
        <span className="auth-badge__name">{user.displayName?.split(' ')[0] ?? 'Account'}</span>
        {isSubscribed && <span className="auth-badge__pro">Pro</span>}
        <svg className="auth-badge__chevron" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M2 3.5l3 3 3-3" />
        </svg>
      </button>

      {open && (
        <div className="auth-badge__dropdown">
          <div className="auth-badge__user-info">
            <div className="auth-badge__user-name">
              {user.displayName ?? 'Rider'}
              {isSubscribed && <span className="auth-badge__pro auth-badge__pro--dropdown">Pro</span>}
            </div>
            <div className="auth-badge__user-email">{user.email}</div>
          </div>
          {profile?.businessVerifiedStatus === 'verified' && (
            <button
              className="auth-badge__menu-item"
              onClick={() => { onOpenDashboard?.(); setOpen(false); }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg>
              Dashboard
            </button>
          )}
          <button
            className="auth-badge__menu-item"
            onClick={() => { onOpenRides?.(); setOpen(false); }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/></svg>
            My Rides
          </button>
          <button
            className="auth-badge__menu-item"
            onClick={() => { onOpenProfile?.(); setOpen(false); }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Profile &amp; Settings
          </button>
          <button
            className="auth-badge__menu-item"
            onClick={() => { setSubOpen(true); setOpen(false); }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/>
            </svg>
            {isSubscribed ? 'Manage subscription' : 'Upgrade to Pro'}
          </button>
          {user.email === ADMIN_EMAIL && (
            <button
              className="auth-badge__menu-item"
              onClick={() => { onOpenAdmin?.(); setOpen(false); }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z"/></svg>
              Admin
            </button>
          )}
          <button
            className="auth-badge__signout"
            onClick={() => { signOut(); setOpen(false); }}
          >
            Sign out
          </button>
        </div>
      )}

      {subOpen && <SubscriptionModal onClose={() => setSubOpen(false)} />}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
